import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react"

interface FeatureData {
  id: string
  title: string
  description: string
  badge: string
  features: string[]
  image: string
  secondImage?: string
  thirdImage?: string
  imagePosition: "left" | "right"
  color: "blue" | "purple" | "green" | "orange" | "pink"
}

const featuresData: FeatureData[] = [
  {
    id: "maps",
    title: "The Ultimate Map Experience",
    description:
      "More than just navigation. Maps are smart collections with deep mode integrations. Share tasks, events, and meetings directly on the map or in chat request panels.",
    badge: "🗺️ Maps • Smart Collections",
    features: [
      "Smart collections & mode integrations",
      "Share tasks, events & meetings",
      "Request panels & chat integration",
      "Post location-based videos & photos",
    ],
    image: "/images/maps-interface.png",
    imagePosition: "right",
    color: "blue",
  },
  {
    id: "entertainment",
    title: "Watch, Listen, & Create",
    description:
      "Upload, watch, and like videos with our signature fire emote 🔥. Create polls, share moments, and enjoy content without limits.",
    badge: "🎬 Video & 📱 Reels",
    features: [
      "Like videos with fire emote 🔥",
      "Create polls, photos & videos",
      "Watch & upload reels",
      "Content creation tools",
    ],
    image: "/images/video-interface.png",
    secondImage: "/images/reels-interface.png",
    imagePosition: "left",
    color: "purple",
  },
  {
    id: "professional",
    title: "Grow & Give Back",
    description:
      "Apply for jobs, post opportunities, or make a difference. Join communities with a public feed on the main page and find volunteering opportunities.",
    badge: "💼 Jobs, 👥 Communities & 🤝 Volunteering",
    features: [
      "Apply & post for jobs",
      "Public community feeds",
      "Add charities & volunteer",
      "Professional networking",
    ],
    image: "/images/jobs-interface.png",
    secondImage: "/images/communities-interface.png",
    thirdImage: "/images/volunteering-interface.png",
    imagePosition: "right",
    color: "blue",
  },
  {
    id: "lifestyle",
    title: "Connect, Vibe & Experience",
    description:
      "Swipe to find new connections, discover local happenings, or get lost in the music. Whether you're looking for love, a concert, or your next favorite song, Z has you covered.",
    badge: "❤️ Dating, 📅 Events & 🎵 Music",
    features: [
      "Swipe to see users & date",
      "Discover & book local events",
      "Integrated music player",
      "Schedule meetings & gatherings",
    ],
    image: "/images/dating-interface.png",
    secondImage: "/images/events-interface.png",
    thirdImage: "/images/music-interface.png",
    imagePosition: "left",
    color: "pink",
  },
  {
    id: "world",
    title: "Explore the World",
    description:
      "Stay informed and plan your next adventure. Watch news, comment on stories, and book flights and hotels all in one place.",
    badge: "📰 News & ✈️ Travel",
    features: ["Watch news & comment", "Book flights & hotels", "Travel planning tools", "Global news coverage"],
    image: "/images/travel-booking-interface.png",
    secondImage: "/images/news-feed-interface.jpg",
    imagePosition: "right",
    color: "green",
  },
  {
    id: "ondemand",
    title: "Life on Demand",
    description:
      "Everything you need, delivered. Buy products, order food, or hail a ride with a single tap. The ultimate convenience.",
    badge: "🛍️ Shop, 🚖 Taxi & 🍔 Food",
    features: ["Shop & buy products", "Order food delivery", "Taxi & ride hailing", "Unified payment system"],
    image: "/images/shopping-interface.png",
    secondImage: "/images/taxi-booking-interface.png",
    thirdImage: "/images/food-delivery-interface.png",
    imagePosition: "left",
    color: "orange",
  },
]

function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ')
}

export function FeatureShowcase() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % featuresData.length)
  }

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + featuresData.length) % featuresData.length)
  }

  const currentFeature = featuresData[currentIndex]

  return (
    <section id="features" className="bg-white py-24 md:py-32 overflow-hidden relative">
      <div className="container mx-auto px-4 md:px-6">
        {/* Navigation Controls */}
        <div className="flex justify-between items-center mb-12 md:mb-20">
          <div className="space-y-2">
            <h2 className="text-3xl md:text-4xl font-bold">Explore Z Modes</h2>
            <p className="text-gray-600">Swipe to discover all the ways Z empowers your life</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={prevSlide}
              className="p-3 rounded-full border border-slate-200 hover:bg-slate-50 hover:border-blue-200 transition-colors"
              aria-label="Previous feature"
            >
              <ChevronLeft className="w-6 h-6 text-slate-600" />
            </button>
            <button
              onClick={nextSlide}
              className="p-3 rounded-full bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all hover:scale-105"
              aria-label="Next feature"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="relative min-h-[600px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentFeature.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
              className="w-full"
            >
              <div
                className={cn(
                  "flex flex-col gap-12 md:gap-24 items-center",
                  currentFeature.imagePosition === "right" ? "md:flex-row" : "md:flex-row-reverse",
                )}
              >
                {/* Text Content */}
                <div className="flex-1 space-y-8">
                  <div>
                    <div
                      className={cn(
                        "inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-6",
                        currentFeature.color === "blue"
                          ? "bg-blue-100 text-blue-700"
                          : currentFeature.color === "purple"
                            ? "bg-purple-100 text-purple-700"
                            : currentFeature.color === "green"
                              ? "bg-green-100 text-green-700"
                              : currentFeature.color === "orange"
                                ? "bg-orange-100 text-orange-700"
                                : "bg-pink-100 text-pink-700",
                      )}
                    >
                      {currentFeature.badge}
                    </div>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">{currentFeature.title}</h2>
                    <p className="text-lg text-gray-600 leading-relaxed mb-8">{currentFeature.description}</p>
                    <ul className="space-y-4">
                      {currentFeature.features.map((feature, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 + 0.3 }}
                          className="flex items-center gap-3"
                        >
                          <div
                            className={cn(
                              "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center",
                              currentFeature.color === "blue"
                                ? "bg-blue-100 text-blue-600"
                                : currentFeature.color === "purple"
                                  ? "bg-purple-100 text-purple-600"
                                  : currentFeature.color === "green"
                                    ? "bg-green-100 text-green-600"
                                    : currentFeature.color === "orange"
                                      ? "bg-orange-100 text-orange-600"
                                      : "bg-pink-100 text-pink-600",
                            )}
                          >
                            <Check className="w-3.5 h-3.5 stroke-[3]" />
                          </div>
                          <span className="font-medium">{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Image Content */}
                <div className="flex-1 w-full flex items-center justify-center">
                  <div className="relative w-full max-w-2xl">
                    <div className="absolute inset-0 bg-gradient-to-tr from-gray-200 to-gray-100 rounded-[2.5rem] transform rotate-3 scale-105 opacity-50" />

                    {currentFeature.thirdImage ? (
                      <div className="relative w-full grid grid-cols-3 gap-2 md:gap-4 items-center justify-items-center">
                        <div
                          className="relative aspect-[9/19] w-full rounded-[1.5rem] overflow-hidden shadow-2xl border border-gray-200 bg-zinc-950 transform transition-transform hover:-translate-y-2 duration-300 cursor-zoom-in flex items-center justify-center p-2"
                          onClick={() => setSelectedImage(currentFeature.image)}
                        >
                          <img
                            src={currentFeature.image}
                            alt={currentFeature.title}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>

                        <div
                          className="relative aspect-[9/19] w-full rounded-[1.5rem] overflow-hidden shadow-2xl border border-gray-200 bg-zinc-950 transform transition-transform hover:-translate-y-2 duration-300 cursor-zoom-in flex items-center justify-center p-2"
                          onClick={() => setSelectedImage(currentFeature.secondImage!)}
                        >
                          <img
                            src={currentFeature.secondImage}
                            alt="Secondary Interface"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>

                        <div
                          className="relative aspect-[9/19] w-full rounded-[1.5rem] overflow-hidden shadow-2xl border border-gray-200 bg-zinc-950 transform transition-transform hover:-translate-y-2 duration-300 cursor-zoom-in flex items-center justify-center p-2"
                          onClick={() => setSelectedImage(currentFeature.thirdImage!)}
                        >
                          <img
                            src={currentFeature.thirdImage}
                            alt="Tertiary Interface"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      </div>
                    ) : currentFeature.secondImage ? (
                      <div className="relative w-full grid grid-cols-2 gap-4 md:gap-8">
                        <div
                          className="relative aspect-[9/19] w-full rounded-[2rem] overflow-hidden shadow-2xl border border-gray-200 bg-zinc-950 transform transition-transform hover:-translate-y-2 duration-300 cursor-zoom-in flex items-center justify-center p-2"
                          onClick={() => setSelectedImage(currentFeature.image)}
                        >
                          <img
                            src={currentFeature.image}
                            alt={currentFeature.title}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>

                        <div
                          className="relative aspect-[9/19] w-full rounded-[2rem] overflow-hidden shadow-2xl border border-gray-200 bg-zinc-950 transform transition-transform hover:-translate-y-2 duration-300 mt-8 md:mt-16 cursor-zoom-in flex items-center justify-center p-2"
                          onClick={() => setSelectedImage(currentFeature.secondImage!)}
                        >
                          <img
                            src={currentFeature.secondImage}
                            alt="Secondary Interface"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      </div>
                    ) : (
                      <div
                        className="relative aspect-[9/19] w-full rounded-[2rem] overflow-hidden shadow-2xl border border-gray-200 bg-zinc-950 transform transition-transform hover:-translate-y-2 duration-300 cursor-zoom-in flex items-center justify-center p-2"
                        onClick={() => setSelectedImage(currentFeature.image)}
                      >
                        <img
                          src={currentFeature.image}
                          alt={currentFeature.title}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center gap-2 mt-12">
          {featuresData.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={cn(
                "w-2.5 h-2.5 rounded-full transition-all duration-300",
                index === currentIndex ? "bg-blue-600 w-8" : "bg-slate-300 hover:bg-slate-400",
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Full Screen Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 md:p-8 backdrop-blur-sm"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-50 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="relative w-full h-full" onClick={(e) => e.stopPropagation()}>
                <img
                  src={selectedImage}
                  alt="Full screen view"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
